import React, { useEffect, useState } from 'react';
import { resolveApiBase } from '../utils/apiBase';

const API_BASE = resolveApiBase();

// SVG Icons for password visibility toggle
const EyeOpenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export default function SignInBar() {
  // This bar now only shows signed-in account + logout. OTP inputs are shown within TryOnForm when credits are exhausted.
  const [me, setMe] = useState(null);
  const [showSetPwd, setShowSetPwd] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [pwStatus, setPwStatus] = useState('');
  const [pwError, setPwError] = useState('');
  const [showBarPwd, setShowBarPwd] = useState(false);

  const fetchMe = async () => {
    try {
      const base = API_BASE || window.location.origin;
      const url = new URL('/me', base);
      const resp = await fetch(url.toString(), { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setMe(data);
      } else {
        setMe(null);
      }
    } catch (_) {
      setMe(null);
    }
  };

  useEffect(() => {
    fetchMe();
    const handler = () => fetchMe();
    window.addEventListener('auth-change', handler);
    return () => window.removeEventListener('auth-change', handler);
  }, []);

  const logout = async () => {
    try {
      const base = API_BASE || window.location.origin;
      const url = new URL('/auth/logout', base);
      await fetch(url.toString(), { method: 'POST', credentials: 'include' });
    } catch (_) { /* ignore */ }
    setMe(null);
    // Notify others to refresh credits
    window.dispatchEvent(new Event('auth-change'));
  };

  const setPassword = async () => {
    try {
      setPwError('');
      setPwStatus('Saving…');
      const base = API_BASE || window.location.origin;
      const url = new URL('/auth/set-password', base);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: newPwd })
      });
      if (!resp.ok) {
        const j = await resp.json().catch(()=>({}));
        throw new Error(j?.detail || `Server ${resp.status}`);
      }
      setPwStatus('Password set');
      setNewPwd('');
      setShowSetPwd(false);
      // Refresh session snapshot so header button label switches to "Change Password"
      try { await fetchMe(); } catch(_) {}
      try { window.dispatchEvent(new Event('auth-change')); } catch(_) {}
      setTimeout(()=>setPwStatus(''), 1200);
    } catch (e) {
      setPwError(e.message || 'Failed to set password');
      setPwStatus('');
    }
  };

  return (
    <div style={{ background: '#f7f7f7', borderBottom: '1px solid #e3e3e3' }}>
      <div style={{
        maxWidth: 980,
        margin: '0 auto',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {me ? (
          <div style={{ marginLeft: 'auto', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            <span style={{fontSize:'1rem'}}>Signed in as <strong>{me.email}</strong> · Credits: <strong>{me.free_credits}</strong></span>
            <button onClick={() => { setShowSetPwd(s => !s); setPwError(''); setPwStatus(''); }}>
              {showSetPwd ? 'Cancel' : (me?.has_password ? 'Change Password' : 'Set Password')}
            </button>
            <button onClick={logout}>Logout</button>
            {showSetPwd && (
              <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', marginLeft: '8px' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>
                  <input
                    type={showBarPwd ? 'text' : 'password'}
                    placeholder="New password (min 8)"
                    value={newPwd}
                    onChange={e => setNewPwd(e.target.value)}
                    style={{ padding:'4px 6px' }}
                  />
                  <button
                    type="button"
                    aria-label={showBarPwd ? 'Hide password' : 'Show password'}
                    title={showBarPwd ? 'Hide password' : 'Show password'}
                    onClick={() => setShowBarPwd(v => !v)}
                    className="mini-btn"
                    style={{ padding:'0.2rem 0.4rem', display:'flex', alignItems:'center', justifyContent:'center' }}
                  >
                    {showBarPwd ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </button>
                </span>
                <button onClick={setPassword} disabled={newPwd.length < 8}>Save</button>
                <span style={{ fontSize:'0.8rem' }}>
                  {pwStatus && <span style={{ color:'#2b6' }}>{pwStatus}</span>}
                  {pwError && <span style={{ color:'#b22', marginLeft:8 }}>{pwError}</span>}
                </span>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
